'use strict';

/**
 * Commerce Client — GA4 dataLayer bridge for the Agentforce messaging widget.
 *
 * Pushes four events:
 *   ai_stylist_open     { open_location }
 *   ai_stylist_query    { query_text }
 *   ai_stylist_response { response_text }
 *   view_item_list      { ecommerce.items[].item_id }
 *
 * A richer variant of this bridge (session id, query numbering, response timing
 * and list/product/suggestion counts) lives on the
 * feature/BXAVSUP-8297-analytics-extended branch, tag ga4-analytics-extended-fields.
 *
 * Bundled into app.js and exposed as window.ccChatAnalytics, which is what
 * cimulate/messagingWidget.isml drives.
 *
 * `open` and `view_item_list` come from the widget's own hooks. `query` and
 * `response` have NO public callback in widget 1.42.1, so they are read off the
 * rendered transcript with a MutationObserver. That markup is not a published
 * contract: re-verify SELECTORS below whenever cc_cdnVersion changes, and use
 * cc_analyticsObserverEnabled to switch this half off from Business Manager
 * without a deploy.
 */

// Transcript restored on page load re-renders past turns; ignore that burst.
var SETTLE_MS = 1500;

// How long a send gesture stays valid as "the next bubble is a live query".
var SEND_GESTURE_TTL_MS = 8000;

var SEEN_LIMIT = 50;

// The widget portals its panel straight into <body>, so the container div it is
// injected into stays empty — everything below has to hang off the panel itself.
var PANEL_SELECTOR = '.cim-widget-container';
var COMPOSER_SELECTOR = '.cim-widget-search-input,.cim-widget-search-bar input,.cim-widget-search-bar textarea';

// Prefixed names are the 1.51.1+ convention, bare names cover older bundles.
var USER_MESSAGE_SELECTOR = [
    '.cim-widget-user-message',
    '.cim-widget-user-message-text',
    '.user-message',
    '.user-message-text'
].join(',');

// The widget announces each completed agent reply in a visually hidden span.
// It carries the markdown prose only, skips restored history, and falls back to
// "Assistant response available" for replies that are products alone. Note it is
// marked role="status", NOT aria-live — the carousel track is the aria-live one.
var RESPONSE_ANNOUNCER_SELECTOR = '.cim-widget-visually-hidden[role="status"],[role="status"][aria-atomic="true"]';

var SUGGESTION_SELECTOR = '.cim-widget-suggestion-button';

// Shopper bubbles the widget sends on its own — not shopper queries.
var AUTO_MESSAGE_PREFIXES = [
    'i completed express payment',
    'i completed the payment using express checkout'
];

var EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
var UK_POSTCODE_PATTERN = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/gi;
var LONG_DIGITS_PATTERN = /\+?\d[\d\s-]{9,}\d/g;
var REDACTED = '[redacted]';

var state = {
    enabled: false,
    observerEnabled: false,
    initialized: false,
    panel: null,
    observer: null,
    bodyObserver: null,
    attachedAt: 0,
    armedUntil: 0,
    openSource: '',
    lastProductSignature: '',
    seen: [],
    liveNodes: [],
    liveValues: []
};

/**
 * @param {NodeList} nodeList - list to copy
 * @returns {Array} plain array of the same nodes
 */
function toArray(nodeList) {
    var out = [];
    for (var i = 0; i < nodeList.length; i++) {
        out.push(nodeList[i]);
    }
    return out;
}

/**
 * @param {string} text - raw text
 * @returns {string} trimmed text with runs of whitespace collapsed
 */
function normalize(text) {
    return String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
}

/**
 * @param {string} text - raw text
 * @returns {string} text with e-mail, postcode and long digit runs redacted
 */
function scrub(text) {
    return String(text == null ? '' : text)
        .replace(EMAIL_PATTERN, REDACTED)
        .replace(UK_POSTCODE_PATTERN, REDACTED)
        .replace(LONG_DIGITS_PATTERN, REDACTED);
}

/**
 * @param {string} text - raw text
 * @returns {string} scrubbed, normalized text
 */
function sanitize(text) {
    return normalize(scrub(text));
}

/**
 * @param {string} text - bubble text
 * @returns {boolean} whether the widget sent this bubble on the shopper's behalf
 */
function isAutoMessage(text) {
    var lower = normalize(text).toLowerCase();
    for (var i = 0; i < AUTO_MESSAGE_PREFIXES.length; i++) {
        if (lower.indexOf(AUTO_MESSAGE_PREFIXES[i]) === 0) {
            return true;
        }
    }
    return false;
}

/**
 * @param {Object} payload - dataLayer entry
 */
function push(payload) {
    if (!state.enabled) {
        return;
    }
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
    } catch (e) {
        // Analytics must never break the chat.
    }
}

/**
 * @param {string} source - storefront entry point about to open the panel
 */
function setOpenSource(source) {
    state.openSource = source ? String(source) : '';
}

/**
 * @param {string} [source] - overrides the entry point recorded by setOpenSource
 */
function trackOpen(source) {
    push({
        event: 'ai_stylist_open',
        open_location: source || state.openSource || 'unknown'
    });
    state.openSource = '';
}

/**
 * @param {Object} detail - messagingConfig.onProductResult payload
 */
function trackProductResult(detail) {
    if (!detail || detail.kind !== 'search' || !detail.productIds) {
        return;
    }

    var items = [];
    var seen = {};
    for (var i = 0; i < detail.productIds.length; i++) {
        var id = detail.productIds[i] ? String(detail.productIds[i]) : '';
        if (id && !seen[id]) {
            seen[id] = true;
            items.push({ item_id: id });
        }
    }
    if (!items.length) {
        return;
    }

    var signature = detail.productIds.join(',');
    if (signature === state.lastProductSignature) {
        return;
    }
    state.lastProductSignature = signature;

    push({ ecommerce: null });
    push({ event: 'view_item_list', ecommerce: { items: items } });
}

function armSendGesture() {
    state.armedUntil = Date.now() + SEND_GESTURE_TTL_MS;
}

/**
 * @returns {boolean} whether the composer currently holds text
 */
function composerHasText() {
    if (!state.panel) {
        return false;
    }
    var fields = state.panel.querySelectorAll(COMPOSER_SELECTOR);
    for (var i = 0; i < fields.length; i++) {
        if (fields[i].value && fields[i].value.trim()) {
            return true;
        }
    }
    return false;
}

/**
 * @param {Node} node - node to test
 * @returns {boolean} whether the node sits inside the chat panel
 */
function isInsidePanel(node) {
    return !!(state.panel && node && node.nodeType === 1 && state.panel.contains(node));
}

/**
 * @param {KeyboardEvent} event - captured keydown
 */
function onKeyDown(event) {
    if (!event || event.key !== 'Enter' || event.shiftKey) {
        return;
    }
    var target = event.target;
    if (!isInsidePanel(target) || (target.tagName !== 'TEXTAREA' && target.tagName !== 'INPUT')) {
        return;
    }
    if (target.value && target.value.trim()) {
        armSendGesture();
    }
}

/**
 * @param {MouseEvent} event - captured click
 */
function onClick(event) {
    if (!event || !isInsidePanel(event.target)) {
        return;
    }
    // Suggestion chips send with an empty composer, so they need their own trigger.
    if (event.target.closest && event.target.closest(SUGGESTION_SELECTOR)) {
        armSendGesture();
        return;
    }
    // Capture phase: the composer is still populated when the send button is hit.
    if (composerHasText()) {
        armSendGesture();
    }
}

/**
 * Collects the outermost shopper-message elements added in a mutation.
 * @param {Node} node - added node
 * @param {Array} out - accumulator
 */
function collectUserMessages(node, out) {
    if (!node || node.nodeType !== 1) {
        return;
    }
    if (node.matches && node.matches(USER_MESSAGE_SELECTOR)) {
        out.push(node);
        return;
    }
    if (!node.querySelectorAll) {
        return;
    }
    var found = node.querySelectorAll(USER_MESSAGE_SELECTOR);
    for (var i = 0; i < found.length; i++) {
        var nested = false;
        for (var j = 0; j < found.length; j++) {
            if (j !== i && found[j].contains(found[i])) {
                nested = true;
                break;
            }
        }
        if (!nested) {
            out.push(found[i]);
        }
    }
}

/**
 * @param {Element} node - live region
 * @param {string} value - its current text
 * @returns {string|null} the value seen previously, or null when first seen
 */
function rememberLiveValue(node, value) {
    var index = state.liveNodes.indexOf(node);
    if (index === -1) {
        state.liveNodes.push(node);
        state.liveValues.push(value);
        return null;
    }
    var previous = state.liveValues[index];
    state.liveValues[index] = value;
    return previous;
}

/**
 * @param {boolean} isSettling - whether the panel is still rendering restored history
 */
function readResponseAnnouncers(isSettling) {
    if (!state.panel) {
        return;
    }
    // Queried from the document: the announcer is not guaranteed to sit inside the panel.
    var regions = toArray(document.querySelectorAll(RESPONSE_ANNOUNCER_SELECTOR));
    for (var i = 0; i < regions.length; i++) {
        var value = normalize(regions[i].textContent);
        var previous = rememberLiveValue(regions[i], value);
        // The announcer is cleared between replies, so every non-empty change is a
        // new completed answer.
        if (isSettling || !value || value === previous) {
            continue;
        }
        push({ event: 'ai_stylist_response', response_text: sanitize(value) });
    }
}

/**
 * @param {Array} candidates - shopper bubbles added in this batch
 * @param {number} now - batch timestamp
 * @param {boolean} isSettling - whether the panel is still rendering restored history
 */
function trackQueryCandidate(candidates, now, isSettling) {
    // More than one bubble in a single batch means a re-rendered transcript.
    if (!candidates.length || isSettling || candidates.length > 1 || now > state.armedUntil) {
        return;
    }

    var element = candidates[0];
    if (state.seen.indexOf(element) !== -1) {
        return;
    }
    state.seen.push(element);
    if (state.seen.length > SEEN_LIMIT) {
        state.seen.shift();
    }

    var raw = element.textContent || '';
    if (isAutoMessage(raw)) {
        return;
    }
    var queryText = sanitize(raw);
    if (!queryText) {
        return;
    }

    state.armedUntil = 0;
    push({ event: 'ai_stylist_query', query_text: queryText });
}

/**
 * @param {Array} records - MutationObserver batch
 */
function handleMutations(records) {
    var now = Date.now();
    var isSettling = now - state.attachedAt < SETTLE_MS;
    var candidates = [];

    for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
            collectUserMessages(added[j], candidates);
        }
    }

    readResponseAnnouncers(isSettling);
    trackQueryCandidate(candidates, now, isSettling);
}

function detach() {
    if (state.observer) {
        state.observer.disconnect();
        state.observer = null;
    }
    state.panel = null;
}

function attach() {
    if (!state.observerEnabled) {
        return;
    }

    var panel = document.querySelector(PANEL_SELECTOR);
    if (!panel) {
        detach();
        return;
    }
    if (state.panel === panel && state.observer) {
        return;
    }

    detach();

    state.panel = panel;
    state.attachedAt = Date.now();
    state.armedUntil = 0;
    state.seen = [];
    state.liveNodes = [];
    state.liveValues = [];

    state.observer = new window.MutationObserver(handleMutations);
    state.observer.observe(panel, { childList: true, subtree: true, characterData: true });
}

/**
 * @param {Object} options - { enabled, observerEnabled } from cc_config
 */
function init(options) {
    var settings = options || {};
    state.enabled = settings.enabled !== false;
    state.observerEnabled = state.enabled
        && settings.observerEnabled !== false
        && typeof window.MutationObserver === 'function';

    if (!state.initialized) {
        state.initialized = true;
        if (state.observerEnabled) {
            document.addEventListener('keydown', onKeyDown, true);
            document.addEventListener('click', onClick, true);
            window.addEventListener('onCimulateWidgetReady', attach);

            // The panel is a direct child of <body>, added on open and removed on close.
            state.bodyObserver = new window.MutationObserver(attach);
            state.bodyObserver.observe(document.body, { childList: true });
        }
    }
    attach();
}

module.exports = {
    init: init,
    attach: attach,
    detach: detach,
    setOpenSource: setOpenSource,
    trackOpen: trackOpen,
    trackProductResult: trackProductResult
};
